import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type ActiveAdvancedFilterState } from '../../../shared/types';
import { type ReportColumnState } from '../../ViolationsReport/data-layer/violationsReportTypes';
import * as apiActions from '../data-layer/entReportsApi';
import { type EntReportDataRaw, type EntReportTabType } from '../types';

export interface ExportBannerState {
  isVisible: boolean;
  title: string;
  description: string;
  colorTheme: 'Success' | 'Critical' | 'Information';
  kind: 'Success' | 'Critical' | 'low' | 'high';
  exportId?: string | null;
  key?: string; // Unique key to force re-rendering
}

export interface ReportColumnTypes {
  label: string;
  value: string;
  isChecked: boolean;
  isDisabled: boolean;
}

export interface EntReportsState {
  isLoading: boolean;
  reportModalTab: string;
  selectedReport: string;
  activeColumns: ReportColumnTypes[];
  reportData: EntReportDataRaw[];
  isReportError: boolean;
  reportErrorReason: string;
  exportBanner: ExportBannerState;
  activeExports: { reportType: string; progress: number }[];
  isPolling: boolean;
  exportProgress: Record<string, number>;
  exportIdsByReportType: Record<string, string>;
  exportStatusByReportType: Record<string, string>;
  completedExportsCount: number;
  lastStatusReportTypes: string[];
  selectedEntitlementValueKey: string | null;
  selectedEntitlementName: string | null;
  riskDetailsFilters: ActiveAdvancedFilterState[];
  userDetailsFilters: ActiveAdvancedFilterState[];
  /** Grouped column definitions from `/view/columns?reportType=…` for export header labels */
  columnsByReportType?: Partial<
    Record<EntReportTabType, Record<string, ReportColumnState[]>>
  >;
}

const initialState: EntReportsState = {
  isLoading: false,
  reportModalTab: '',
  selectedReport: '',
  activeColumns: [],
  reportData: [],
  isReportError: false,
  reportErrorReason: '',
  exportBanner: {
    isVisible: false,
    title: '',
    description: '',
    colorTheme: 'Success',
    kind: 'Success',
    exportId: null,
    key: undefined,
  },
  activeExports: [],
  isPolling: false,
  exportProgress: {},
  exportIdsByReportType: {},
  exportStatusByReportType: {},
  completedExportsCount: 0,
  lastStatusReportTypes: [],
  selectedEntitlementValueKey: null,
  selectedEntitlementName: null,
  riskDetailsFilters: [],
  userDetailsFilters: [],
  columnsByReportType: {},
};

const entReportsSlice = createSlice({
  name: 'aag/entReports',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSelectedReport: (state, action: PayloadAction<string>) => {
      state.selectedReport = action.payload;
    },
    setReportModalTab: (state, action: PayloadAction<string>) => {
      state.reportModalTab = action.payload;
    },
    setReportColumns: (state, action: PayloadAction<ReportColumnTypes[]>) => {
      state.activeColumns = action.payload;
    },
    setReportError: (
      state,
      action: PayloadAction<{ state: boolean; reason?: string }>
    ) => {
      state.isReportError = action.payload.state;
      state.reportErrorReason = action.payload.reason ?? '';
    },
    setEntReportData: (state, action: PayloadAction<EntReportDataRaw[]>) => {
      state.reportData = action.payload;
    },
    setExportBanner: (state, action: PayloadAction<ExportBannerState>) => {
      const newBanner = {
        ...action.payload,
        key: `banner-${Date.now()}-${Math.random()}`,
      };

      state.exportBanner = newBanner;
    },
    hideExportBanner: (state) => {
      state.exportBanner.isVisible = false;
    },
    addActiveExport: (
      state,
      action: PayloadAction<{ reportType: string; progress?: number } | string>
    ) => {
      const payload =
        typeof action.payload === 'string'
          ? { reportType: action.payload, progress: 0 }
          : {
              reportType: action.payload.reportType,
              progress: action.payload.progress ?? 0,
            };

      const existing = state.activeExports.find(
        (e) => e.reportType === payload.reportType
      );

      if (!existing) {
        state.activeExports.push(payload);
      } else {
        existing.progress = payload.progress;
      }
    },
    removeActiveExport: (state, action: PayloadAction<string>) => {
      state.activeExports = state.activeExports.filter(
        (e) => e.reportType !== action.payload
      );
    },
    setExportProgress: (
      state,
      action: PayloadAction<{ reportType: string; progressPercentage: number }>
    ) => {
      const { reportType, progressPercentage } = action.payload;

      state.exportProgress[reportType] = progressPercentage;
    },
    setExportIdForReportType: (
      state,
      action: PayloadAction<{
        reportType: string;
        exportId?: string | null;
        status?: string;
      }>
    ) => {
      const { reportType, exportId, status } = action.payload;

      if (exportId) {
        state.exportIdsByReportType[reportType] = exportId;
      }

      if (status) {
        state.exportStatusByReportType[reportType] = status;
      }
    },
    setIsPolling: (state, action: PayloadAction<boolean>) => {
      state.isPolling = action.payload;
    },
    clearCompletedExports: (state) => {
      const newProgress: Record<string, number> = {};
      const newIds: Record<string, string> = {};
      const newStatus: Record<string, string> = {};

      Object.keys(state.exportProgress).forEach((reportType) => {
        const progress = state.exportProgress[reportType];

        if (typeof progress === 'number' && progress < 100) {
          newProgress[reportType] = progress;

          if (state.exportIdsByReportType[reportType]) {
            newIds[reportType] = state.exportIdsByReportType[reportType];
          }

          if (state.exportStatusByReportType[reportType]) {
            newStatus[reportType] = state.exportStatusByReportType[reportType];
          }
        }
      });

      state.exportProgress = newProgress;
      state.exportIdsByReportType = newIds;
      state.exportStatusByReportType = newStatus;
    },
    setCompletedExportsCount: (state, action: PayloadAction<number>) => {
      state.completedExportsCount = action.payload;
    },
    setLastStatusReportTypes: (state, action: PayloadAction<string[]>) => {
      state.lastStatusReportTypes = action.payload;
    },
    setSelectedEntitlementValueKey: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedEntitlementValueKey = action.payload;
    },
    setSelectedEntitlementName: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedEntitlementName = action.payload;
    },
    setRiskDetailsFilters: (
      state,
      action: PayloadAction<ActiveAdvancedFilterState[]>
    ) => {
      state.riskDetailsFilters = action.payload;
    },
    setUserDetailsFilters: (
      state,
      action: PayloadAction<ActiveAdvancedFilterState[]>
    ) => {
      state.userDetailsFilters = action.payload;
    },
    setColumnsForReportType: (
      state,
      action: PayloadAction<{
        reportType: EntReportTabType;
        columns: Record<string, ReportColumnState[]>;
      }>
    ) => {
      const { reportType, columns } = action.payload;

      if (!state.columnsByReportType) {
        state.columnsByReportType = {};
      }

      state.columnsByReportType[reportType] = columns;
    },
  },
});

const { reducer: entReportsReducer, actions: sliceActions } = entReportsSlice;

export const entReportsActions = {
  ...sliceActions,
  ...apiActions,
};
export default entReportsReducer;
